import { useState, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ArrowLeft, ExternalLink, ShieldCheck, RefreshCw, Sparkles,
  CheckCircle2, ChevronDown, Zap, Mic, Brain, Bot, MessageSquare,
} from "lucide-react";
import { findEmrBySlug } from "@/config/emrs";
import { findEmrPage } from "@/config/emrPages";
import { EmrDemoForm } from "@/components/landing/EmrDemoForm";
import { ModalShell } from "@/components/landing/ModalShell";

/* Dedicated /integrations/:slug page for live EMRs (Juvonno, Jane App).
   Layout: Hero → Outcomes strip → How it works → What syncs (all capabilities)
   → AI voice & conversation → Spotlight (Juvonno exec dash / Jane warmth)
   → Data transfer → FAQ → Inline EMR-specific book form. */

const HOW_STEPS = [
  { n: "01", title: "Connect", desc: "Authorize Clinic OS once. We map your locations, clinicians and appointment types." },
  { n: "02", title: "Sync",    desc: "Initial backfill of patients, schedule and active conversations. Usually under an hour." },
  { n: "03", title: "Go live", desc: "AI Receptionist picks up calls, Inbox shows every channel, Pulse starts firing automations." },
  { n: "04", title: "Refine",  desc: "We tune AI personality, recall triggers and clinician rules over your first 14 days." },
];

const AI_FEATURES = [
  { Icon: Mic,            title: "AI voice that sounds like your front desk",
    desc: "Natural, warm and on-brand. Answers after-hours, weekends, and overflow without missing context." },
  { Icon: Brain,          title: "Real-time availability checks",
    desc: "Reads {EMR_NAME} live. If a slot opens or closes mid-call, the AI knows it before the next sentence." },
  { Icon: Bot,            title: "Booking, rescheduling, cancellations",
    desc: "Patients can book, reschedule or cancel right on the phone. Every change writes straight to {EMR_NAME}." },
  { Icon: MessageSquare,  title: "Smart follow-up handoffs",
    desc: "If a caller needs a human — pricing, complex cases — the AI captures intent and routes to your team." },
];

export default function IntegrationPage() {
  const { slug } = useParams();
  const emr = findEmrBySlug(slug);
  const page = findEmrPage(slug);
  const [openFaq, setOpenFaq] = useState(0);
  const [showDemo, setShowDemo] = useState(false);
  const [showMiniNav, setShowMiniNav] = useState(false);

  // Mobile-only: slim sticky mini-nav appears after scrolling past the hero
  useEffect(() => {
    const onScroll = () => setShowMiniNav(window.scrollY > 220);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!emr || emr.status !== "live" || !page) {
    return <Navigate to="/integrations" replace />;
  }

  const gradient = `linear-gradient(135deg, ${page.hero.brandFromHex}, ${page.hero.brandToHex})`;
  const textGradient = { backgroundImage: `linear-gradient(90deg, ${page.hero.brandFromHex}, ${page.hero.brandToHex})` };

  return (
    <article data-testid={`emr-page-${emr.slug}`} className="overflow-hidden">

      {/* ───────── MOBILE STICKY MINI-NAV — rolls up on scroll ───────── */}
      <AnimatePresence>
        {showMiniNav && (
          <motion.div
            initial={{ y: -42, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -42, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            data-testid="emr-mini-nav"
            className="fixed inset-x-0 top-16 z-30 flex items-center justify-between gap-2 border-b border-border/70 bg-background/95 px-4 py-2 backdrop-blur sm:hidden"
          >
            <Link
              to="/integrations"
              data-testid="emr-mini-back"
              className="inline-flex items-center gap-1.5 text-[12px] font-bold text-foreground active:scale-[0.97]"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              <img src={emr.logo} alt="" className="h-4 w-4 rounded object-contain" />
              <span className="font-display tracking-tight">{emr.name}</span>
              <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-1.5 py-px text-[9px] font-bold uppercase tracking-[0.12em] text-emerald-700 dark:text-emerald-300">
                <span className="h-1 w-1 rounded-full bg-emerald-500" />
                Live
              </span>
            </Link>
            <button
              type="button"
              onClick={() => setShowDemo(true)}
              data-testid="emr-mini-book"
              className="inline-flex h-8 items-center gap-1 rounded-lg px-2.5 text-[11px] font-bold text-white shadow-sm active:scale-[0.97]"
              style={{ backgroundImage: gradient }}
            >
              Book demo
              <ArrowRight className="h-3 w-3" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ───────────── HERO ───────────── */}
      <section className="relative pt-20 pb-8 sm:pt-32 sm:pb-16">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-96 opacity-30"
          style={{ background: `radial-gradient(50% 70% at 50% 0%, ${page.hero.brandFromHex}33, transparent 70%)` }}
        />

        <div className="mx-auto max-w-6xl px-5 sm:px-8 lg:px-12">
          <Link
            to="/integrations"
            data-testid="emr-back"
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            All integrations
          </Link>

          <div className="mt-4 flex flex-col items-start gap-4 sm:mt-6 sm:gap-6 lg:flex-row lg:items-center lg:gap-10">
            {/* EMR logo card — smaller on mobile */}
            <div className="flex h-14 w-14 flex-none items-center justify-center rounded-2xl border border-border bg-card p-2 shadow-sm sm:h-24 sm:w-24 sm:p-3">
              <img src={emr.logo} alt={`${emr.name} logo`} className="h-full w-full object-contain" />
            </div>

            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  </span>
                  Live integration
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Native · real-time 2-way sync
                </span>
              </div>

              <h1 className="mt-3 font-display text-[2rem] font-black leading-[1.05] tracking-[-0.025em] sm:mt-4 sm:text-5xl lg:text-6xl">
                Clinic OS{" "}
                <span className="bg-clip-text text-transparent" style={textGradient}>
                  + {emr.name}.
                </span>
              </h1>
              <p className="mt-3 max-w-2xl text-[14px] leading-relaxed text-muted-foreground sm:mt-4 sm:text-lg">
                {page.hero.sub}
              </p>

              {/* Suited */}
              {emr.suited && (
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    Suited for
                  </span>
                  {emr.suited.map((s) => (
                    <span
                      key={s}
                      className="rounded-full border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground/80"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              )}

              {/* CTAs */}
              <div className="mt-7 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:flex-wrap">
                <button
                  type="button"
                  onClick={() => setShowDemo(true)}
                  data-testid="emr-hero-book"
                  style={{ backgroundImage: gradient }}
                  className="group inline-flex h-12 items-center justify-center gap-2 rounded-xl px-7 text-sm font-bold text-white shadow-lg ring-1 ring-inset ring-white/15 transition-all hover:-translate-y-0.5 hover:shadow-xl"
                >
                  Book your {emr.name} walk-through
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </button>
                {page.spotlight.kind === "exec-dashboard" && (
                  <Link
                    to="/juvonno/clinicpulse"
                    data-testid="emr-hero-pulse"
                    className="group inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-violet-500/50 bg-violet-500/10 px-6 text-sm font-bold text-violet-700 transition-all hover:-translate-y-0.5 hover:bg-violet-500/15 dark:text-violet-300"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    Walk through Clinic Pulse
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                )}
                <a
                  href={emr.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="emr-visit"
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-border bg-card px-7 text-sm font-semibold text-foreground transition-all hover:-translate-y-0.5 hover:border-foreground/30"
                >
                  Visit {emr.name} <ExternalLink className="h-3.5 w-3.5 opacity-60" />
                </a>
              </div>
            </div>
          </div>

          {/* Outcome stat strip */}
          <div className="mt-12 grid gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {page.outcomes.map((o, i) => (
              <motion.div
                key={o.label}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                data-testid={`emr-outcome-${i}`}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-foreground/25 hover:shadow-md"
              >
                <div
                  className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full opacity-10 blur-3xl transition-opacity group-hover:opacity-30"
                  style={{ backgroundImage: gradient }}
                />
                <p
                  className="relative bg-clip-text font-display text-3xl font-black tracking-tight text-transparent sm:text-4xl"
                  style={textGradient}
                >
                  {o.stat}
                </p>
                <p className="relative mt-1 text-[13px] font-semibold leading-tight">
                  {o.label}
                </p>
                <p className="relative mt-0.5 text-[11px] leading-snug text-muted-foreground">
                  {o.foot}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── HOW IT WORKS ───────────── */}
      <section className="py-20 sm:py-24" data-testid="emr-how">
        <div className="mx-auto max-w-6xl px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              How it works
            </p>
            <h2 className="mt-3 font-display text-3xl font-black tracking-[-0.02em] sm:text-4xl lg:text-5xl">
              Live in days,{" "}
              <span className="bg-clip-text text-transparent" style={textGradient}>not months.</span>
            </h2>
            <p className="mt-4 max-w-xl leading-relaxed text-muted-foreground">
              {emr.short}
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {HOW_STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.07 }}
                data-testid={`emr-how-${s.n}`}
                className="relative rounded-2xl border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:border-foreground/25 hover:shadow-lg"
              >
                <span
                  className="absolute -top-3 left-6 inline-flex items-center rounded-full px-2.5 py-0.5 font-mono text-[10px] font-bold text-white shadow-md ring-1 ring-inset ring-white/15"
                  style={{ backgroundImage: gradient }}
                >
                  Step {s.n}
                </span>
                <h3 className="mt-3 font-display text-xl font-bold tracking-tight">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {s.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── WHAT SYNCS — driven by emrPages.capabilities ───────────── */}
      <section className="border-t border-border bg-secondary/30 py-20 dark:bg-transparent sm:py-24" data-testid="emr-sync">
        <div className="mx-auto max-w-6xl px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              What stays in sync
            </p>
            <h2 className="mt-3 font-display text-3xl font-black tracking-[-0.02em] sm:text-4xl lg:text-5xl">
              Every patient signal,{" "}
              <span className="bg-clip-text text-transparent" style={textGradient}>in lockstep.</span>
            </h2>
            <p className="mt-4 max-w-xl leading-relaxed text-muted-foreground">
              The patient hangs up, the booking is already in {emr.name}. The form submits, the chart is already created. Nothing falls through the cracks.
            </p>
          </div>

          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {page.capabilities.map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.32, delay: (i % 6) * 0.04 }}
                data-testid={`emr-cap-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:border-foreground/25 hover:shadow-lg"
              >
                <div
                  className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full opacity-0 blur-3xl transition-opacity duration-300 group-hover:opacity-30"
                  style={{ backgroundImage: gradient }}
                />
                <div className="relative flex items-center gap-2">
                  <span
                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-white shadow-sm ring-1 ring-inset ring-white/15"
                    style={{ backgroundImage: gradient }}
                  >
                    <c.Icon className="h-5 w-5" />
                  </span>
                  {c.pill && (
                    <span
                      className="ml-auto rounded-full px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.12em] text-white shadow-sm"
                      style={{ backgroundImage: gradient }}
                    >
                      {c.pill}
                    </span>
                  )}
                </div>
                <h3 className="relative mt-4 font-display text-lg font-bold tracking-tight">
                  {c.title}
                </h3>
                <p className="relative mt-1.5 text-sm leading-relaxed text-muted-foreground">
                  {c.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── AI VOICE & CONVERSATION (sticky left) ───────────── */}
      <section className="py-20 sm:py-24" data-testid="emr-ai">
        <div className="mx-auto max-w-6xl px-6 sm:px-8 lg:px-12">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5 lg:sticky lg:top-28 lg:self-start">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                AI voice & conversation
              </p>
              <h2 className="mt-3 font-display text-3xl font-black tracking-[-0.02em] sm:text-4xl lg:text-5xl">
                The AI Receptionist{" "}
                <span className="bg-clip-text text-transparent" style={textGradient}>knows {emr.name}.</span>
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                Not a generic bot. Tuned to your clinic, your team and the rules
                inside your {emr.name} account — so it answers like a senior front-desk
                lead who has been there for years.
              </p>

              <div
                className="mt-7 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs ring-1 ring-inset"
                style={{ background: `${page.hero.brandFromHex}10`, borderColor: `${page.hero.brandFromHex}33`, color: page.hero.brandFromHex }}
              >
                <Sparkles className="h-3.5 w-3.5" />
                <span className="font-semibold">Avg. answer rate after launch: 97%+</span>
              </div>
            </div>

            <div className="space-y-5 lg:col-span-7">
              {AI_FEATURES.map((f, i) => (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  data-testid={`emr-ai-${i}`}
                  className="flex gap-4 rounded-2xl border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:border-foreground/25 hover:shadow-lg"
                >
                  <span
                    className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-xl text-white shadow-sm ring-1 ring-inset ring-white/15"
                    style={{ backgroundImage: gradient }}
                  >
                    <f.Icon className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="font-display text-lg font-bold tracking-tight">
                      {f.title.replaceAll("{EMR_NAME}", emr.name)}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                      {f.desc.replaceAll("{EMR_NAME}", emr.name)}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── SPOTLIGHT (Juvonno exec dashboard / Jane warmth) ───────────── */}
      <section data-testid="emr-spotlight" className="relative overflow-hidden border-t border-border bg-secondary/30 py-20 dark:bg-transparent sm:py-24">
        <div
          className="pointer-events-none absolute -left-32 top-10 h-72 w-72 rounded-full opacity-20 blur-3xl"
          style={{ backgroundImage: gradient }}
        />
        <div className="relative mx-auto max-w-6xl px-6 sm:px-8 lg:px-12">
          <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-6">
              <p
                className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.22em]"
                style={{ borderColor: `${page.hero.brandFromHex}55`, color: page.hero.brandFromHex, background: `${page.hero.brandFromHex}10` }}
              >
                <Sparkles className="h-3 w-3" />
                {page.spotlight.eyebrow}
              </p>
              <h2 className="mt-4 font-display text-3xl font-black leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
                {page.spotlight.title}
              </h2>
              <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-muted-foreground sm:text-base">
                {page.spotlight.body}
              </p>
              <ul className="mt-6 space-y-2.5">
                {page.spotlight.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" style={{ color: page.hero.brandFromHex }} />
                    <span className="text-[14px] leading-snug text-foreground/85">{b}</span>
                  </li>
                ))}
              </ul>

              {/* CTAs — Juvonno gets full walkthrough link; Jane gets a "Coming Q2 2026" badge */}
              {page.spotlight.kind === "exec-dashboard" ? (
                <Link
                  to="/juvonno/clinicpulse"
                  data-testid="emr-spotlight-walkthrough"
                  style={{ backgroundImage: gradient }}
                  className="group mt-8 inline-flex h-12 items-center gap-2 rounded-xl px-6 text-[13px] font-bold text-white shadow-lg ring-1 ring-inset ring-white/15 transition-all hover:-translate-y-0.5 hover:shadow-xl"
                >
                  Walk through the full Clinic Pulse experience
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              ) : (
                <div
                  data-testid="emr-spotlight-exec-soon"
                  className="mt-8 flex flex-wrap items-center gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-4"
                >
                  <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-amber-500/15 text-amber-600 dark:text-amber-400">
                    <Sparkles className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-[13.5px] font-black tracking-tight">
                      Executive Reporting Dashboard for Jane — coming Q2 2026
                    </p>
                    <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">
                      Multi-location KPIs, AI insights and the Location Summary already live for Juvonno clinics. We&apos;re building the same layer on top of Jane next quarter.
                    </p>
                  </div>
                  <Link
                    to="/juvonno/clinicpulse"
                    data-testid="emr-spotlight-jane-preview"
                    className="inline-flex h-9 flex-none items-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 text-[11.5px] font-bold text-amber-700 transition-all hover:-translate-y-0.5 dark:text-amber-300"
                  >
                    Preview the Juvonno version
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              )}
            </div>

            {/* Right column — Juvonno: exec dashboard mock · Jane: AI booking mock */}
            <div className="lg:col-span-6">
              <div className="relative overflow-hidden rounded-3xl border border-border bg-[#0a0a14] p-6 shadow-2xl sm:p-8">
                <div
                  className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full opacity-40 blur-3xl"
                  style={{ backgroundImage: gradient }}
                />

                {page.spotlight.kind === "exec-dashboard" ? (
                  <>
                    <div className="relative flex items-center justify-between">
                      <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-white/60">
                        Executive · live · {emr.name}
                      </p>
                      <span className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold text-emerald-400">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                        Refreshed 2m ago
                      </span>
                    </div>

                    <div className="relative mt-5 grid grid-cols-3 gap-3 text-white">
                      {[
                        { k: "Revenue · MTD", v: "$184.2k", d: "+12.4%" },
                        { k: "Utilisation",    v: "76%",     d: "+4.1%" },
                        { k: "No-show rate",   v: "6.8%",    d: "−2.3%" },
                      ].map((s) => (
                        <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3.5">
                          <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-white/55">{s.k}</p>
                          <p className="mt-1.5 font-display text-xl font-black tracking-tight">{s.v}</p>
                          <p className="mt-0.5 text-[10.5px] font-semibold text-emerald-400">{s.d}</p>
                        </div>
                      ))}
                    </div>

                    <div className="relative mt-5 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">
                        Bookings · last 7 days
                      </p>
                      <div className="mt-3 flex h-24 items-end gap-2">
                        {[42, 58, 47, 72, 63, 88, 76].map((h, idx) => (
                          <div key={idx} className="flex-1 rounded-t-md ring-1 ring-inset ring-white/10"
                            style={{ height: `${h}%`, backgroundImage: gradient, opacity: 0.85 }}
                          />
                        ))}
                      </div>
                      <div className="mt-2 flex justify-between text-[9.5px] font-semibold uppercase tracking-[0.14em] text-white/40">
                        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => (<span key={d}>{d}</span>))}
                      </div>
                    </div>

                    <div className="relative mt-5 grid grid-cols-2 gap-3">
                      {[
                        { k: "Top location",      v: "Mississauga", d: "$38.4k MTD" },
                        { k: "Best practitioner", v: "Sarah Chen",  d: "94% utilisation" },
                      ].map((s) => (
                        <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3.5">
                          <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-white/55">{s.k}</p>
                          <p className="mt-1.5 font-display text-[14px] font-black tracking-tight text-white">{s.v}</p>
                          <p className="mt-0.5 text-[10.5px] text-white/60">{s.d}</p>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  /* AI booking mock — for Jane (and any future EMR using kind: "ai-booking") */
                  <>
                    <div className="relative flex items-center justify-between">
                      <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-white/60">
                        AI Receptionist · live · {emr.name}
                      </p>
                      <span className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold text-emerald-400">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                        </span>
                        On a call now
                      </span>
                    </div>

                    {/* Mock chat transcript */}
                    <div className="relative mt-5 space-y-2.5">
                      {[
                        { who: "ai",      txt: "Hi, this is Aria at Apex Wellness — how can I help today?" },
                        { who: "patient", txt: "I'd like to book a 60-minute massage with Sarah this week." },
                        { who: "ai",      txt: "Sarah has Tuesday 2:00pm or Thursday 5:30pm — which works?" },
                        { who: "patient", txt: "Tuesday 2pm please." },
                        { who: "ai",      txt: "Booked. Confirmation just sent. You'll get a reminder from Jane the day before." },
                      ].map((m, i) => (
                        <div key={i} className={`flex ${m.who === "patient" ? "justify-end" : "justify-start"}`}>
                          <div
                            className={
                              m.who === "ai"
                                ? "max-w-[80%] rounded-2xl rounded-tl-sm bg-white/[0.08] px-3 py-2 text-[11.5px] leading-snug text-white ring-1 ring-inset ring-white/10"
                                : "max-w-[80%] rounded-2xl rounded-tr-sm px-3 py-2 text-[11.5px] leading-snug text-white shadow-md"
                            }
                            style={m.who === "patient" ? { backgroundImage: gradient } : undefined}
                          >
                            {m.txt}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Bottom action confirmation */}
                    <div className="relative mt-5 grid grid-cols-3 gap-3">
                      {[
                        { k: "Booked",   v: "Tue · 2:00 PM", d: "60-min · Sarah" },
                        { k: "Written to Jane", v: "✓ Synced", d: "2.1s after hangup" },
                        { k: "Patient profile", v: "Updated", d: "Phone · email · tag" },
                      ].map((s) => (
                        <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3">
                          <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-white/55">{s.k}</p>
                          <p className="mt-1 font-display text-[13px] font-black tracking-tight text-white">{s.v}</p>
                          <p className="mt-0.5 text-[10px] text-white/60">{s.d}</p>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── DATA TRANSFER ───────────── */}
      <section className="py-20 sm:py-24" data-testid="emr-transfer">
        <div className="mx-auto max-w-6xl px-6 sm:px-8 lg:px-12">
          <div className="grid gap-10 rounded-3xl border border-border bg-card p-8 sm:p-12 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-6">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                Data transfer
              </p>
              <h2 className="mt-3 font-display text-3xl font-black tracking-[-0.02em] sm:text-4xl">
                Safe, auditable,{" "}
                <span className="bg-clip-text text-transparent" style={textGradient}>always two-way.</span>
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                We never own your data. {emr.name} stays the source of truth — Clinic OS reads, writes and reconciles. Every transfer is encrypted, logged, and revocable.
              </p>

              <ul className="mt-7 space-y-2.5 text-sm">
                {[
                  "TLS 1.3 in transit · AES-256 at rest",
                  "Per-clinic isolated tenants — never shared",
                  "Full audit log of every read/write",
                  "Disconnect in one click — your data stays in your EMR",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2.5">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-emerald-500" />
                    <span className="text-foreground/85">{line}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-6">
              <div className="rounded-2xl border border-border bg-secondary/40 p-6">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Compliance snapshot
                </p>
                <div className="mt-4 grid grid-cols-2 gap-2 text-center">
                  {[
                    { Icon: ShieldCheck, label: "HIPAA-ready" },
                    { Icon: Zap,         label: "SOC 2 aligned" },
                  ].map((b) => (
                    <div key={b.label} className="rounded-xl border border-border bg-card p-3">
                      <b.Icon className="mx-auto h-5 w-5 text-emerald-500" />
                      <p className="mt-2 text-[11px] font-semibold leading-tight">{b.label}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-5 rounded-xl border border-border bg-card p-4 text-xs">
                  <p className="font-semibold">Latency</p>
                  <p className="mt-1 text-muted-foreground">
                    Writes appear in {emr.name} typically in{" "}
                    <span className="font-semibold text-foreground">&lt; 2 seconds</span>.
                    Reads cached at the edge for &lt; 200 ms response.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── FAQ ───────────── */}
      <section data-testid="emr-faq" className="border-t border-border py-20 sm:py-24">
        <div className="mx-auto max-w-3xl px-6 sm:px-8 lg:px-12">
          <p
            className="text-[10.5px] font-bold uppercase tracking-[0.22em]"
            style={{ color: page.hero.brandFromHex }}
          >
            Common questions
          </p>
          <h2 className="mt-3 font-display text-3xl font-black leading-[1.06] tracking-tight sm:text-4xl">
            Quick answers before the call.
          </h2>

          <div className="mt-8 divide-y divide-border rounded-2xl border border-border bg-card">
            {page.faq.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <button
                  key={f.q}
                  type="button"
                  onClick={() => setOpenFaq(isOpen ? -1 : i)}
                  data-testid={`emr-faq-${i}`}
                  className="w-full text-left"
                >
                  <div className="flex items-start justify-between gap-4 px-5 py-4 sm:px-6 sm:py-5">
                    <h3 className="font-display text-[15px] font-bold tracking-tight sm:text-base">
                      {f.q}
                    </h3>
                    <ChevronDown
                      className={`mt-0.5 h-4 w-4 flex-none text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`}
                    />
                  </div>
                  {isOpen && (
                    <p className="px-5 pb-5 text-[13.5px] leading-relaxed text-muted-foreground sm:px-6 sm:pb-6">
                      {f.a}
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───────────── FINAL CTA ───────────── */}
      <section id="book" data-testid="emr-book-section" className="scroll-mt-24 border-t border-border bg-secondary/30 py-16 dark:bg-transparent sm:py-20">
        <div className="mx-auto max-w-4xl px-6 text-center sm:px-8 lg:px-12">
          <p
            className="text-[11px] font-bold uppercase tracking-[0.22em]"
            style={{ color: page.hero.brandFromHex }}
          >
            Ready in 60 seconds
          </p>
          <h2 className="mt-3 font-display text-3xl font-black leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            See your own{" "}
            <span className="bg-clip-text text-transparent" style={textGradient}>{emr.name}</span>{" "}
            clinic running on Clinic OS.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            Drop your details — we&apos;ll connect your {emr.name} account and walk you through a personalised demo with your real numbers. No slides.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => setShowDemo(true)}
              data-testid="emr-final-book"
              style={{ backgroundImage: gradient }}
              className="group inline-flex h-12 items-center gap-2 rounded-xl px-7 text-sm font-bold text-white shadow-lg ring-1 ring-inset ring-white/15 transition-all hover:-translate-y-0.5 hover:shadow-xl"
            >
              Book my {emr.name} walk-through
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            {page.spotlight.kind === "exec-dashboard" && (
              <Link
                to="/juvonno/clinicpulse"
                data-testid="emr-final-pulse"
                className="inline-flex h-12 items-center gap-2 rounded-xl border border-violet-500/50 bg-violet-500/10 px-6 text-sm font-bold text-violet-700 transition-all hover:-translate-y-0.5 hover:bg-violet-500/15 dark:text-violet-300"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Walk through Clinic Pulse first
              </Link>
            )}
          </div>
          <p className="mt-5 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            HIPAA &amp; SOC 2 · Disconnect anytime · Live in &lt; 14 days
          </p>
        </div>
      </section>

      {/* ── Demo modal ───────────────────────────────────────────────── */}
      <AnimatePresence>
        {showDemo && (
          <ModalShell onClose={() => setShowDemo(false)} wide>
            <div className="p-3 sm:p-4">
              <EmrDemoForm emr={emr} page={page} compact />
            </div>
          </ModalShell>
        )}
      </AnimatePresence>
    </article>
  );
}
